import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { spas } from '../data/charlotteSpas';
import SpaCard from '../components/SpaCard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiNavigation, FiInfo } = FiIcons;

// Custom Icon for Leaflet
const customIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/2776/2776067.png',
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
});

// Component to handle map centering
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom, {
      animate: true,
      duration: 1.5
    });
  }, [center, zoom, map]);
  return null;
}

const Neighborhoods = () => {
  const [activeNeighborhood, setActiveNeighborhood] = useState('Uptown');
  const [mapCenter, setMapCenter] = useState([35.2271, -80.8431]);
  const [zoom, setZoom] = useState(14);
  
  const neighborhoods = [...new Set(spas.map(s => s.neighborhood))];
  const filteredSpas = spas.filter(s => s.neighborhood === activeNeighborhood);

  const handleNeighborhoodChange = (area) => {
    setActiveNeighborhood(area);
    const areaSpas = spas.filter(s => s.neighborhood === area);
    if (areaSpas.length > 0) {
      setMapCenter([areaSpas[0].lat, areaSpas[0].lng]);
      setZoom(area === 'Ballantyne' ? 13 : 15);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf9] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-16 text-center">
          <h1 className="font-playfair text-5xl font-bold text-stone-900 mb-6">Charlotte Districts</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Navigate the city's premier wellness landscape with our live interactive map.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Neighborhood List */}
          <div className="lg:col-span-4 space-y-3 h-fit">
            <h3 className="text-xs font-bold text-stone-400 uppercase tracking-[0.2em] mb-6 px-4">Select Area</h3>
            {neighborhoods.map((area) => (
              <button
                key={area}
                onClick={() => handleNeighborhoodChange(area)}
                className={`w-full text-left px-6 py-5 rounded-2xl transition-all flex items-center justify-between group ${
                  activeNeighborhood === area 
                  ? 'bg-indigo-900 text-white shadow-xl font-bold scale-[1.02]' 
                  : 'bg-white text-stone-600 hover:bg-stone-50 border border-stone-200'
                }`}
              >
                <div>
                  <span className="block text-lg">{area}</span>
                  <span className={`text-xs block mt-1 ${activeNeighborhood === area ? 'text-indigo-200' : 'text-stone-400'}`}>
                    {spas.filter(s => s.neighborhood === area).length} Locations
                  </span>
                </div>
                <SafeIcon icon={FiNavigation} className={`w-5 h-5 ${activeNeighborhood === area ? 'text-indigo-300' : 'text-stone-300 group-hover:text-indigo-400'}`} />
              </button>
            ))}
          </div>

          {/* Map & Results */}
          <div className="lg:col-span-8 space-y-12">
            {/* Real Map Interface */}
            <div className="bg-white rounded-[3rem] p-4 border-4 border-white shadow-2xl relative overflow-hidden h-[550px] z-10">
              <MapContainer 
                center={mapCenter} 
                zoom={zoom} 
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%', borderRadius: '2rem' }}
              >
                <ChangeView center={mapCenter} zoom={zoom} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {spas.map((spa) => (
                  <Marker 
                    key={spa.id} 
                    position={[spa.lat, spa.lng]} 
                    icon={customIcon}
                  >
                    <Popup className="rounded-2xl overflow-hidden">
                      <div className="w-48">
                        <img src={spa.imageUrl} className="w-full h-24 object-cover rounded-lg mb-2" alt="" />
                        <h4 className="font-bold text-stone-900 leading-tight">{spa.name}</h4>
                        <p className="text-[10px] text-stone-500 uppercase mt-1">{spa.type}</p>
                        <p className="text-[10px] text-indigo-700 font-bold mt-2">{spa.address}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              {/* Map Overlay Info */}
              <div className="absolute top-8 left-8 bg-white/95 backdrop-blur-md p-6 rounded-3xl shadow-2xl border border-stone-100 min-w-[240px] z-[400]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Live Interactive Map</p>
                </div>
                <p className="font-playfair text-3xl font-bold text-indigo-900">{activeNeighborhood}</p>
                <div className="mt-4 pt-4 border-t border-stone-100 text-stone-500 text-xs flex items-center gap-2">
                  <SafeIcon icon={FiInfo} className="w-4 h-4" />
                  Click markers for street-level details
                </div>
              </div>
            </div>

            {/* Results for Neighborhood */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredSpas.map(spa => (
                 <SpaCard key={spa.id} spa={spa} />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Map Helper Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        .leaflet-container {
          background: #f0f0f0;
          font-family: 'Inter', sans-serif;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 1rem !important;
          padding: 0 !important;
          overflow: hidden;
        }
        .leaflet-popup-content {
          margin: 0 !important;
          padding: 12px !important;
        }
        .leaflet-bar {
          border: none !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
        }
        .leaflet-bar a {
          background-color: white !important;
          color: #1e1b4b !important;
          border-bottom: 1px solid #f0f0f0 !important;
        }
      `}} />
    </div>
  );
};

export default Neighborhoods;